import { useEffect, useRef, useState } from "react"

import { Button, Form } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { EditOutlined, EyeOutlined } from "@/icons"
import { FormError } from "@/organisms/Ticketing/components/JiraTemplateModal/JiraTemplateModal.types"
import { TicketModal } from "@/organisms/Ticketing/components/TicketModal"
import { TicketTestModal } from "@/organisms/Ticketing/components/TicketTestModal"
import { useServerFieldErrors } from "@/organisms/Ticketing/hooks/useServerFieldErrors"
import { useTemplateTest } from "@/organisms/Ticketing/hooks/useTemplateTest"
import { useTicketingMetadata } from "@/organisms/Ticketing/hooks/useTicketingMetadata"
import { JiraTemplateModalFields } from "."
import {
  filterOnDemandFieldsWithValues,
  processValidationErrors
} from "./helpers"

import type { JiraTemplateFormValues, JiraTemplateModalProps } from "."

export const JiraTemplateModal = (props: JiraTemplateModalProps) => {
  const {
    type,
    closeModal,
    existingTemplate,
    validationErrors,
    testConfig,
    extra,
    hasWritePermissions
  } = props
  const { t } = useTranslation("translation", {
    keyPrefix: "jiraTemplateModal"
  })

  const [formError, setFormError] = useState<FormError | null>(null)
  const [hasFieldErrors, setHasFieldErrors] = useState(false)
  const [previewMode, setPreviewMode] = useState(!hasWritePermissions)
  const [form] = Form.useForm<JiraTemplateFormValues>()
  const initialValues = useRef<JiraTemplateFormValues | null>(null)
  const { setServerErrors, createServerErrorRule } = useServerFieldErrors()

  // Test flow state
  const { testModalState, setTestModalState, confirmTest } = useTemplateTest({
    form,
    t,
    setFormError,
    setServerErrors,
    onFieldErrors: () => setHasFieldErrors(true)
  })

  const { onTest, isLoading: isTestLoading = false } = testConfig ?? {}

  const {
    projectId,
    issueTypeId,
    projects,
    issueTypesOptions,
    allFields,
    fieldsMetadata,
    metadataIsLoading,
    fieldsIsLoading,
    url,
    handleProjectChange
  } = useTicketingMetadata(form)

  // Reset initialization when modal closes
  useEffect(() => {
    if (type !== "jira") {
      initialValues.current = null
      setFormError(null)
    }
  }, [type, validationErrors])

  // Step 1: Set project and issue type first (this determines which fields to render)
  useEffect(() => {
    if (type === "jira" && !metadataIsLoading && projects.length > 0) {
      const projectToSet = existingTemplate?.project || projects[0].id
      const selectedProject = projects.find(
        (p: Jira.Project) => p.id === projectToSet
      )
      const issueTypeToSet =
        existingTemplate?.issuetype || selectedProject?.issuetypes[0]?.id

      form.setFieldsValue({
        project: projectToSet,
        issuetype: issueTypeToSet
      })
    }
  }, [type, metadataIsLoading, projects, form, existingTemplate])

  useEffect(() => {
    if (
      existingTemplate &&
      !initialValues.current &&
      !metadataIsLoading &&
      !fieldsIsLoading &&
      projectId &&
      issueTypeId
    ) {
      initialValues.current = { ...existingTemplate }
      const fieldsToSet = Object.entries(existingTemplate).map(
        ([key, value]) => ({
          name: key,
          value,
          errors: [] as string[]
        })
      )

      // If validation errors exist, store them and update the errors for those fields
      if (validationErrors) {
        const { formErrors, fieldErrors, hasFieldErrors } =
          processValidationErrors(validationErrors, t)

        // Create a map of field keys to field metadata
        const fieldsMap = allFields.reduce(
          (acc, field) => {
            acc[field.key] = field
            return acc
          },
          {} as Record<string, Jira.IssueTypeField>
        )

        // Store server errors and update errors for fields that have validation errors
        const additionalFormErrors: string[] = []
        const fieldServerErrors: Record<string, string> = {}
        Object.entries(fieldErrors).forEach(([fieldKey, errorMessage]) => {
          const fieldMetadata = fieldsMap[fieldKey]
          if (fieldMetadata) {
            const fieldToUpdate = fieldsToSet.find((f) => f.name === fieldKey)

            if (fieldToUpdate) {
              // Field exists in form - set field-specific error
              fieldServerErrors[fieldKey] = errorMessage
              fieldToUpdate.errors = [errorMessage]
            } else {
              // Field not in form (not rendered) - add to form-level errors
              additionalFormErrors.push(errorMessage)
            }
          } else {
            // Field metadata not found - add raw error to form-level errors
            additionalFormErrors.push(errorMessage)
          }
        })

        setServerErrors(fieldServerErrors, initialValues.current ?? {})

        if (hasFieldErrors) {
          setHasFieldErrors(true)
        }

        const allFormErrors = [...formErrors, ...additionalFormErrors]
        if (allFormErrors.length > 0) {
          setFormError(allFormErrors.join("; "))
        }
      }

      // Set all fields (values + errors) in one call
      form.setFields(fieldsToSet)
    }
  }, [projectId, issueTypeId, metadataIsLoading, fieldsIsLoading])

  useEffect(() => {
    // Only clear form errors when project/issue type change AFTER initialization
    if (
      initialValues.current &&
      projectId &&
      issueTypeId &&
      (initialValues.current.project !== projectId ||
        initialValues.current.issuetype !== issueTypeId)
    ) {
      setFormError(null)
    }
  }, [issueTypeId, projectId])

  const handleFieldsChange = () => {
    const errors = form.getFieldsError()
    const hasErrors = errors.some((field) => field.errors.length > 0)
    setHasFieldErrors(hasErrors)
    if (!hasErrors) {
      setFormError(null)
    }
  }

  const handleSubmit = async () => {
    try {
      await form.validateFields()
      // If onTest is provided, show confirmation modal instead of directly closing
      if (onTest) {
        setTestModalState("open")
      } else {
        const fieldsValues = form.getFieldsValue()
        fieldsValues.onDemandFields = filterOnDemandFieldsWithValues(
          fieldsValues.onDemandFields
        )
        closeModal(fieldsValues, fieldsMetadata)
      }
    } catch {
      setFormError(t("error.completeRequiredFields"))
    }
  }

  const handleConfirmTest = async () => {
    if (!onTest) return

    const fieldsValues = form.getFieldsValue()
    fieldsValues.onDemandFields = filterOnDemandFieldsWithValues(
      fieldsValues.onDemandFields
    )

    await confirmTest(
      () => onTest(fieldsValues, fieldsMetadata),
      initialValues.current ?? {}
    )
  }

  const handleCloseTestModal = () => {
    setTestModalState(null)
  }

  const handleSuccessOk = () => {
    // Save the template and close both modals
    const finalFieldsValues = form.getFieldsValue()
    finalFieldsValues.onDemandFields = filterOnDemandFieldsWithValues(
      finalFieldsValues.onDemandFields
    )
    setTestModalState(null)
    closeModal(finalFieldsValues, fieldsMetadata)
  }

  return (
    <>
      <TicketModal
        mode="template"
        type={type}
        metadataIsLoading={metadataIsLoading}
        fieldsIsLoading={fieldsIsLoading}
        formError={formError}
        onCancel={() => closeModal()}
        onSubmit={handleSubmit}
        submitDisabled={fieldsIsLoading || hasFieldErrors}
        url={url}
        form={form}
        projects={projects}
        issueTypesOptions={issueTypesOptions}
        onFieldsChange={handleFieldsChange}
        onProjectChange={handleProjectChange}
        initialSummary={
          existingTemplate?.summary || extra?.defaultIssueTemplate.summary || ""
        }
        initialDescription={
          existingTemplate?.description ||
          extra?.defaultIssueTemplate.description ||
          ""
        }
        tags={extra?.dynamicTags}
        createServerErrorRule={createServerErrorRule}
        setPreviewMode={setPreviewMode}
        footer={
          <Button
            disabled={!hasWritePermissions}
            type="link"
            icon={previewMode ? <EditOutlined /> : <EyeOutlined />}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {t(`mode.${previewMode ? "edit" : "preview"}`)}
          </Button>
        }
        previewMode={previewMode}
      >
        <JiraTemplateModalFields
          previewMode={previewMode}
          disabled={previewMode}
          projectId={projectId}
          issueTypeId={issueTypeId}
          createServerErrorRule={createServerErrorRule}
          form={form}
          tags={extra?.dynamicTags}
          existingTemplate={existingTemplate}
        />
      </TicketModal>

      <TicketTestModal
        state={testModalState}
        isLoading={isTestLoading}
        onClose={handleCloseTestModal}
        onConfirmTest={handleConfirmTest}
        onSuccessOk={handleSuccessOk}
      />
    </>
  )
}
